import React, { Component } from "react";
import { connect } from "react-redux";
import { isEqual, get, noop, flowRight as compose } from "lodash";
import { graphql } from "@apollo/client/react/hoc";
import { getFilteredRaces } from "@tvg/utils/pastPerformanceUtils";
import type {
  NullaryFn,
  UnaryFn,
  BinaryFn,
  QuadFn
} from "@tvg/ts-types/Functional";
import type { WagerProfile } from "@tvg/ts-types/User";
import type { Device } from "@tvg/ts-types/Device";
import {
  emptyMessagesDefault,
  mtpNotificationConfigDefault,
  handicappingRunnerFlagsDefault
} from "@tvg/atomic-ui/__mocks__/ipp-pastRaces.mock";
import Container from "@tvg/atomic-ui/_static/BaseComponents/Containers";
import ModalColumns from "@tvg/atomic-ui/_static/BaseComponents/ModalColumns/";
import type { HandicappingRunnerFlags } from "@tvg/ipp/src/components/RunnerFlags";
import type {
  PastPerformanceRace,
  RaceCardBettingInterest,
  RaceProgram,
  RaceTypeCodeEnum,
  Stat,
  MTPNotificationConfigType,
  RaceCardRunner
} from "@tvg/ts-types/Race";
import RunnersPastPerformanceTemplate, {
  renderEmpty
} from "@tvg/atomic-ui/_templates/RunnersPastPerformance";
import parseCapiMessage from "@tvg/utils/capiUtils";
import getRaceAtiveRunnerBI from "@tvg/utils/getRaceAtiveRunner";
import { Dispatch } from "redux";
import HorseSwitcher from "../components/HorseSwitcher";

import InlinePastPerformanceQuery from "../graphql/queries/inlinePastPerformance";
import RunnerStatsQuery from "../graphql/queries/inlinePastPerformanceRunnerStats";
import {
  ApolloOptionsIPP,
  ApolloOptionsRunnerStats
} from "../graphql/options.graph";
import { EmptyMessageType } from "../types";

export type Props = {
  isLoading: boolean;
  isLoadingStats: boolean;
  runnerName: string;
  runnerDob: number;
  entityRunnerId: string;
  races: PastPerformanceRace[];
  originRaceConditions: {
    distance: string | undefined;
    surface: string | undefined;
  };
  wagerProfile: WagerProfile;
  device: Device;
  runners: RaceCardBettingInterest[];
  raceTypeCode: RaceTypeCodeEnum;
  runnerStats: Stat[];
  currentRace: RaceProgram;
  refetch: UnaryFn<unknown | undefined, void>;
  refetchStats: UnaryFn<unknown | undefined, void>;
  onRunnerClick: QuadFn<string, string, string, number, void>;
  onRaceFromList: BinaryFn<number, PastPerformanceRace[], void>;
  onMatchConditions: NullaryFn<void>;
  onModalClose: NullaryFn<void>;
  isMatchConditionsOn: boolean;
  pastPerformanceRaceIndex: number;
  isSilkShown: boolean;
  borderless: boolean;
  noRunnerCommentMessage: string;
  pastPerformanceMatchText: string;
  emptyMessages: EmptyMessageType;
  children: unknown | undefined;
  handicappingRunnerFlags: HandicappingRunnerFlags;
  mtpNotificationConfig: MTPNotificationConfigType;
  handicappingMTPNotification: boolean;
  isHorseSwitcherDisabled: boolean;
  onClickVideo: NullaryFn<void>;
};

type State = {
  races: PastPerformanceRace[];
  filteredRaces: PastPerformanceRace[];
  activeRunnerBI: RaceCardBettingInterest | undefined | null;
};

export class RunnersPastPerformanceComponent extends Component<Props, State> {
  static defaultProps = {
    isLoading: true,
    isLoadingStats: true,
    runnerName: "",
    runnerDob: 0,
    entityRunnerId: "",
    races: [],
    originRaceConditions: {
      distance: null,
      surface: null
    },
    wagerProfile: "PORT-Generic",
    device: "mobile",
    runners: [],
    raceTypeCode: "T",
    runnerStats: [],
    currentRace: {},
    refetch: noop,
    refetchStats: noop,
    onRunnerClick: noop,
    onRaceFromList: noop,
    onMatchConditions: noop,
    onModalClose: noop,
    isMatchConditionsOn: false,
    pastPerformanceRaceIndex: -1,
    isSilkShown: true,
    borderless: false,
    noRunnerCommentMessage: "",
    pastPerformanceMatchText: "",
    children: null,
    emptyMessages: emptyMessagesDefault,
    handicappingRunnerFlags: handicappingRunnerFlagsDefault,
    mtpNotificationConfig: mtpNotificationConfigDefault,
    handicappingMTPNotification: false,
    isHorseSwitcherDisabled: true,
    onClickVideo: noop
  };

  state = {
    races: [],
    filteredRaces: [],
    activeRunnerBI: null
  };

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const {
      races,
      runnerStats,
      currentRace,
      runnerName,
      isLoading,
      isLoadingStats,
      isMatchConditionsOn,
      isSilkShown,
      isHorseSwitcherDisabled
    } = this.props;

    return (
      isLoading !== nextProps.isLoading ||
      isLoadingStats !== nextProps.isLoadingStats ||
      runnerName !== nextProps.runnerName ||
      isMatchConditionsOn !== nextProps.isMatchConditionsOn ||
      isSilkShown !== nextProps.isSilkShown ||
      isHorseSwitcherDisabled !== nextProps.isHorseSwitcherDisabled ||
      !isEqual(this.state, nextState) ||
      !isEqual(runnerStats, nextProps.runnerStats) ||
      !isEqual(races, nextProps.races) ||
      !isEqual(currentRace, nextProps.currentRace) ||
      !isEqual(this.props.children, nextProps.children)
    );
  }

  static getDerivedStateFromProps(props: Props) {
    return {
      activeRunnerBI: getRaceAtiveRunnerBI(props.runners, props.runnerName)
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.isLoading !== prevProps.isLoading) {
      const filteredResults = getFilteredRaces(
        this.props.races,
        this.props.originRaceConditions
      );

      const filteredRaces = filteredResults.races;
      const races = this.props.isMatchConditionsOn
        ? filteredRaces
        : this.props.races;

      // eslint-disable-next-line
      this.setState(
        {
          races,
          filteredRaces
        },
        () => {
          if (
            this.props.device !== "mobile" &&
            !isEqual(races, prevProps.races) &&
            races.length > 0
          ) {
            this.onRaceFromList(0);
          }
        }
      );
    } else if (
      this.props.device !== "mobile" &&
      this.props.isMatchConditionsOn !== prevProps.isMatchConditionsOn &&
      this.state.races.length > 0
    ) {
      this.onRaceFromList(0);
    }
  }

  onMatchConditions = () => {
    const isMatchConditionsOn = !this.props.isMatchConditionsOn;
    const races = isMatchConditionsOn
      ? this.state.filteredRaces
      : this.props.races;
    this.setState({ races });

    this.props.onMatchConditions();
  };

  onRaceFromList = (raceIndex: number) => {
    this.props.onRaceFromList(raceIndex, this.state.races);
  };

  onRunnerClick = (
    runnerName: string,
    entityRunnerId: string,
    index: number
  ) => {
    let clickedRunner: RaceCardRunner | null | undefined = null;
    this.props.runners.forEach((bi) => {
      if (!clickedRunner) {
        clickedRunner = bi.runners.find(
          (runner) => runner.horseName === runnerName
        );
      }
    });

    const runnerDob = get(clickedRunner, "dob");
    this.setState(
      {
        races: [],
        filteredRaces: []
      },
      () => {
        this.props.refetch({
          runnerName,
          runnerDob,
          entityRunnerId
        });
      }
    );

    this.props.onRunnerClick(runnerName, `${runnerDob}`, entityRunnerId, index);
  };

  renderList = () => (
    <RunnersPastPerformanceTemplate
      races={this.state.races}
      totalRaces={this.props.races.length}
      matchQuantity={this.state.filteredRaces.length}
      runnerStats={this.props.runnerStats}
      runners={this.props.runners}
      onRace={this.onRaceFromList}
      selectedRace={this.props.pastPerformanceRaceIndex}
      borderless={this.props.borderless}
      onMatchToggle={this.onMatchConditions}
      isMatchConditionsOn={this.props.isMatchConditionsOn}
      isLoadingRunnerPP={this.props.isLoading}
      isLoadingStats={this.props.isLoadingStats}
      isMobile={this.props.device === "mobile"}
      raceTypeCode={this.props.raceTypeCode}
      activeRunner={this.props.runnerName}
      onModalClose={this.props.onModalClose}
      onRunnerClick={this.onRunnerClick}
      postRaceReportShown
      currentRace={this.props.currentRace}
      noRunnerCommentMessage={this.props.noRunnerCommentMessage}
      pastPerformanceMatchText={this.props.pastPerformanceMatchText}
      emptyMessages={this.props.emptyMessages}
      isSilkShown={this.props.isSilkShown}
      activeRunnerBI={this.state.activeRunnerBI}
      handicappingRunnerFlags={this.props.handicappingRunnerFlags}
      mtpNotificationConfig={this.props.mtpNotificationConfig}
      handicappingMTPNotification={
        this.props.device === "mobile" && this.props.handicappingMTPNotification
      }
      isHorseSwitcherDisabled={this.props.isHorseSwitcherDisabled}
      onClickVideo={this.props.onClickVideo}
      runnerDob={this.props.runnerDob}
    />
  );

  renderNotMobile = () => (
    <Container.GridWrapper>
      <HorseSwitcher
        runners={this.props.runners}
        onRunnerClick={this.onRunnerClick}
        activeRunner={this.props.runnerName}
        raceTypeCode={this.props.raceTypeCode}
      />
      <ModalColumns.GridHolder>
        <ModalColumns.Grid>
          <ModalColumns.Column side="left">
            {this.renderList()}
          </ModalColumns.Column>
          <ModalColumns.Column side="right">
            {(this.state.races.length > 0 && this.props.children) ||
              renderEmpty(
                this.props.emptyMessages,
                this.props.races.length,
                this.props.isMatchConditionsOn,
                this.onMatchConditions
              )}
          </ModalColumns.Column>
        </ModalColumns.Grid>
      </ModalColumns.GridHolder>
    </Container.GridWrapper>
  );

  render() {
    return this.props.device === "mobile"
      ? this.renderList()
      : this.renderNotMobile();
  }
}

export default connect(
  /* istanbul ignore next */
  (store) => ({
    noRunnerCommentMessage: get(
      store,
      "capi.messages.noRunnerCommentMessage",
      ""
    ),
    pastPerformanceMatchText: get(
      store,
      "capi.messages.pastPerformanceMatchText",
      ""
    ),
    emptyMessages: parseCapiMessage(
      store,
      "capi.messages.runnersPastPerformanceEmptyMessages",
      emptyMessagesDefault
    ),
    handicappingRunnerFlags: parseCapiMessage(
      store,
      "capi.messages.handicappingRunnerFlags"
    ),
    mtpNotificationConfig: parseCapiMessage(
      store,
      "capi.messages.mtpNotification"
    ),
    handicappingMTPNotification: get(
      store,
      "capi.featureToggles.handicappingMTPNotification",
      false
    )
  }),
  /* istanbul ignore next */
  (dispatch: Dispatch) => ({ dispatch })
)(
  compose(
    // @ts-ignore
    graphql(InlinePastPerformanceQuery, ApolloOptionsIPP),
    // @ts-ignore
    graphql(RunnerStatsQuery, ApolloOptionsRunnerStats)
  )(RunnersPastPerformanceComponent)
);
